import "./App.css";
import React, { Suspense, useEffect, useState } from "react";
import Menu from "./components/Menu";
import { Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Login from "./Auth";
import { jwtDecode } from "jwt-decode";
import "react-toastify/dist/ReactToastify.css";
import { CircularProgress } from "@mui/material";
import Alert from "./components/Alert";
import RouteProgress from "./components/RouteProgress";

// Lazy load route components for code splitting
const CategoryComponent = React.lazy(() => import("./components/CategoryComponent"));
const AddItemComponent = React.lazy(() => import("./components/AddItemComponent"));
const ArchivedComponent = React.lazy(() => import("./components/ArchivedComponent"));
const ArchivedItemComponent = React.lazy(() => import("./components/ArchivedItemComponent"));
const EditItemComponent = React.lazy(() => import("./components/EditItemComponent"));
const ViewItemComponent = React.lazy(() => import("./components/ViewItemComponent"));
const AgencyManagement = React.lazy(() => import("./components/AgencyManagement"));
const AgentManagement = React.lazy(() => import("./components/AgentManagement"));
const SuperAdminDashboard = React.lazy(() => import("./components/SuperAdminDashboard"));
// Merged into NewReportsDashboard ("Report Builder" tab) 2026-10-26.
// const ReportDashboard = React.lazy(() => import("./components/reports/ReportDashboard"));
const NewReportsDashboard = React.lazy(() => import("./components/reports/NewReportsDashboard"));
const PushNotifications = React.lazy(() => import("./components/notifications/PushNotifications"));
const PackagesDashboard = React.lazy(() => import("./components/packages/PackagesDashboard"));
// Hidden from the dashboard 2026-09-10 at the client's request. The component and its
// API service are still on disk, so restoring it is uncommenting these two places.
// const CustomPackagesDashboard = React.lazy(() => import("./components/packages/CustomPackagesDashboard"));
const CompanyPlansPage = React.lazy(() => import("./components/companyPlans/CompanyPlansPage"));
const ExclusiveListingsPage = React.lazy(() => import("./components/companyPlans/ExclusiveListingsPage"));
const CompanyDetail = React.lazy(() => import("./components/companyPlans/CompanyDetail"));
const PlanLeadsPage = React.lazy(() => import("./components/companyPlans/PlanLeadsPage"));
const PartnersPage = React.lazy(() => import("./components/companyPlans/PartnersPage"));
const PlansPage = React.lazy(() => import("./components/companyPlans/plans/PlansPage"));
const PlanEditPage = React.lazy(() => import("./components/companyPlans/plans/PlanEditPage"));
const PackageEditPage = React.lazy(() => import("./components/packages/PackageEditPage"));
const PlatformAnalyticsPage = React.lazy(() => import("./components/reports/PlatformAnalyticsPage"));
const AuditLogs = React.lazy(() => import("./components/auditLogs/AuditLogs"));
const AdminAccess = React.lazy(() => import("./components/adminAccess/AdminAccess"));

// Loading fallback component
const PageLoader = () => (
    <div className="w-full h-full flex items-center justify-center bg-white dark:bg-gray-800">
        <CircularProgress thickness={6} size={40} sx={{ color: 'var(--primary-color)' }} />
    </div>
);

function App() {
    const isModalOpen = useSelector((state) => state.modal.isOpen);
    const isAlertOpen = useSelector((state) => state.alert.isOpen);
    const location = useLocation();
    const isLoginPage = location.pathname === "/login";
    const token = localStorage.getItem("prona_userToken");
    const [isAdmin, setIsAdmin] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const navigate = useNavigate();
    const isBrandSelectModalOpen = useSelector((state) => state.accessoryBrandSelect.isOpen);

    useEffect(() => {
        if (token) {
            const user = jwtDecode(token);
            setUserRole(user.role);
            if (user.role === "super_admin" || user.role === "admin" || user.role === "company_admin") {
                setIsAdmin(true);
            }
        }
    }, [token]);

    // If we're on the login page and there's no token, show the login page
    if (isLoginPage && !token) {
        return <Login />;
    }

    // If we're on the login page but have a token, redirect to home
    if (isLoginPage && token) {
        return <Navigate to="/" />;
    }

    // If we're not on the login page and have no token, redirect to login
    if (!isLoginPage && !token) {
        return <Navigate to="/login" />;
    }

    // If we have a token, show the appropriate dashboard
    if (token) {
        /* Scoped admins get the same route table as a super_admin. What they may
           actually reach is decided in two places that agree with each other: the
           sidebar hides sections they lack (Menu.js -> usePermissions), and every
           endpoint answers 403 without the grant (middleware/checkPermission.js).
           Giving them their own smaller table here would be a third copy of that
           rule, and the one most likely to drift. */
        if (userRole === "super_admin" || userRole === "admin") {
            return (
                <div className="flex relative bg-white dark:bg-gray-900">
                    <Alert />
                    {/* Top progress bar on navigation — the sidebar used to highlight the
                        new page while the screen still showed the old one. */}
                    <RouteProgress />
                    <div className="md:h-screen md:p-4 md:sticky md:top-0">
                        <Menu />
                    </div>
                    <div className="flex-grow min-w-0 bg-white dark:bg-gray-900 p-4 pt-[72px] md:pt-4 md:pl-0 overflow-hidden">
                        <div className="w-full h-full border border-gray-200 dark:border-gray-900 rounded-xl relative overflow-hidden bg-white dark:bg-gray-900">
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/" element={<SuperAdminDashboard />} />
                                    {/* ONE Reports page (2026-10-26): the old report builder is now the
                                        "Report Builder" tab of NewReportsDashboard; /reports-v2 redirects. */}
                                    <Route path="/reports" element={<NewReportsDashboard />} />
                                    <Route path="/reports-v2" element={<Navigate to="/reports" replace />} />
                                    {/* Platform analytics - full page, matching the veturascout dashboard.
                                        Still reachable from the Reports modal as well. */}
                                    <Route path="/platform-analytics" element={<PlatformAnalyticsPage />} />
                                    {/* Audit / activity log - super_admin only, matching veturascout. */}
                                    <Route path="/audit-logs" element={<AuditLogs />} />
                                    {/* Roles and per-section grants - super_admin only. */}
                                    <Route path="/admin-access" element={<AdminAccess />} />
                                    <Route path="/notifications" element={<PushNotifications />} />
                                    <Route path="/packages" element={<PackagesDashboard />} />
                                    {/* Package editor pages — above the /:category catch-alls. */}
                                    <Route path="/packages/add" element={<PackageEditPage />} />
                                    <Route path="/packages/:id/edit" element={<PackageEditPage />} />
                                    {/* Hidden from the dashboard 2026-09-10 at the client's request. */}
                                    {/* <Route path="/custom-packages" element={<CustomPackagesDashboard />} /> */}
                                    {/* Company plans — MUST stay above the /:category catch-all below,
                                        otherwise CategoryComponent swallows /company-plans. */}
                                    <Route path="/company-plans" element={<CompanyPlansPage />} />
                                    <Route path="/exclusive-listings" element={<ExclusiveListingsPage />} />
                                    <Route path="/company-plans/companies/:id" element={<CompanyDetail />} />
                                    <Route path="/plan-leads" element={<PlanLeadsPage />} />
                                    <Route path="/partners" element={<PartnersPage />} />
                                    {/* Plan catalogue + editor, like the veturascout Plans screens.
                                        Above the /:category catch-alls, which would swallow both. */}
                                    <Route path="/plans" element={<PlansPage />} />
                                    {/* Pako Tregtare (individuals) on its own page, like veturascout's
                                        /trader-package: the same list filtered to the individual line. */}
                                    <Route path="/trader-plans" element={<PlansPage audience="individual" />} />
                                    <Route path="/plans/:id/edit" element={<PlanEditPage />} />
                                    {/* Re-enabled 2026-09-10. The screen was always built; only the
                                        route was a redirect. /agency/:id is what AgencyManagement's own
                                        agencyDetailPath() links to, and CompanyDetail reads params.id. */}
                                    <Route path="/agency" element={<AgencyManagement />} />
                                    <Route path="/agency/:id" element={<CompanyDetail />} />
                                    <Route path="/agent" element={<AgentManagement />} />
                                    <Route path="/:category" element={<CategoryComponent />} />
                                    <Route path="/:category/add" element={<AddItemComponent />} />
                                    <Route path="/:category/archived" element={<ArchivedComponent />} />
                                    <Route path="/:category/archived/:id" element={<ArchivedItemComponent />} />
                                    <Route path="/:category/:id/edit" element={<EditItemComponent />} />
                                    <Route path="/:category/:id/" element={<ViewItemComponent />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
            );
        }

        if (userRole === "marketing") {
            return (
                <div className="flex relative bg-white dark:bg-gray-900">
                    <Alert />
                    <RouteProgress />
                    <div className="md:h-screen md:p-4 md:sticky md:top-0">
                        <Menu />
                    </div>
                    <div className="flex-grow min-w-0 bg-white dark:bg-gray-900 p-4 pt-[72px] md:pt-4 md:pl-0 overflow-hidden">
                        <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-xl relative overflow-hidden bg-gray-50 dark:bg-gray-800">
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/reports" element={<NewReportsDashboard />} />
                                    <Route path="/reports-v2" element={<Navigate to="/reports" replace />} />
                                    <Route path="/:category" element={<CategoryComponent />} />
                                    <Route path="/:category/:id/edit" element={<EditItemComponent />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
            );
        }

        if (userRole === "company_admin") {
            return (
                <div className="flex relative bg-white dark:bg-gray-900">
                    <Alert />
                    <RouteProgress />
                    <div className="md:h-screen md:p-4 md:sticky md:top-0">
                        <Menu />
                    </div>
                    <div className="flex-grow min-w-0 bg-white dark:bg-gray-900 p-4 pt-[72px] md:pt-4 md:pl-0 overflow-hidden">
                        <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-xl relative overflow-hidden bg-gray-50 dark:bg-gray-800">
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    {/* TODO: Re-enable Agency route when requirements are finalized */}
                                    <Route path="/" element={<AgentManagement />} />
                                    <Route path="/agency" element={<Navigate to="/" />} />
                                    <Route path="/agent" element={<AgentManagement />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
            );
        }

        if (userRole === "agent") {
            return (
                <div className="flex relative bg-white dark:bg-gray-900">
                    <Alert />
                    <RouteProgress />
                    <div className="md:h-screen md:p-4 md:sticky md:top-0">
                        <Menu />
                    </div>
                    <div className="flex-grow min-w-0 bg-white dark:bg-gray-900 p-4 pt-[72px] md:pt-4 md:pl-0 overflow-hidden">
                        <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-xl relative overflow-hidden bg-gray-50 dark:bg-gray-800">
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/" element={<AgentManagement />} />
                                    <Route path="/agent" element={<AgentManagement />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex relative bg-white dark:bg-gray-900">
                <Alert />
                <RouteProgress />
                <div className="md:h-screen md:p-4 md:sticky md:top-0">
                    <Menu />
                </div>
                <div className="flex-grow bg-white dark:bg-gray-900 p-4 pt-[72px] md:pt-4 md:pl-0">
                    <div className="w-full h-full border border-gray-200 dark:border-gray-700 rounded-xl relative overflow-hidden bg-gray-50 dark:bg-gray-800">
                        <Suspense fallback={<PageLoader />}>
                            <Routes>
                                <Route path="/" element={<CategoryComponent />} />
                                <Route path="/:category" element={<CategoryComponent />} />
                                <Route path="/:category/add" element={<AddItemComponent />} />
                                <Route path="/:category/archived" element={<ArchivedComponent />} />
                                <Route path="/:category/:id/edit" element={<EditItemComponent />} />
                            </Routes>
                        </Suspense>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

export default App;
